import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  DEFAULT_DENSITY,
  DEFAULT_PRESET_ID,
  DEFAULT_RADIUS,
  type ColorMode,
  type Density,
} from './presets';

/**
 * SidebarPlacement — which edge of the AppShell the sidebar docks to.
 * Top means a horizontal nav bar instead of a vertical column.
 */
export type SidebarPlacement = 'left' | 'right' | 'top';

/**
 * SidebarStyle — how strongly the sidebar uses the brand color.
 *   - 'bold'   : current default. Deep dark sidebar with brand hue tint.
 *   - 'brand'  : sidebar IS the brand color at mid-saturation. Vivid.
 *   - 'subtle' : light/white sidebar with brand color used only on actives.
 */
export type SidebarStyle = 'bold' | 'brand' | 'subtle';

/**
 * Custom primary color override, expressed as an HSL string (matching
 * the format used in globals.css). When set, the preset's `primary` is
 * overridden but every other token from the preset still applies.
 */
export interface CustomColor {
  primary: string | null;
}

/**
 * The subset of ThemeState that's persisted to the server (and to localStorage
 * via the persist middleware). Excludes action functions and any transient
 * runtime flags.
 */
export interface SerializedTheme {
  presetId: string;
  mode: ColorMode;
  density: Density;
  radius: string;
  sidebarPlacement: SidebarPlacement;
  sidebarStyle: SidebarStyle;
  sidebarCollapsed: boolean;
  custom: CustomColor;
}

interface ThemeState extends SerializedTheme {
  /**
   * True while the per-user UI settings GET is in flight (right after login).
   * AppShell can suppress the debounced save during this window so the initial
   * hydration doesn't echo back to the server.
   */
  isHydrating: boolean;

  setPreset: (presetId: string) => void;
  setMode: (mode: ColorMode) => void;
  setDensity: (density: Density) => void;
  setRadius: (radius: string) => void;
  setSidebarPlacement: (placement: SidebarPlacement) => void;
  setSidebarStyle: (style: SidebarStyle) => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleSidebar: () => void;
  setCustomPrimary: (primary: string | null) => void;
  /**
   * Overwrite the persisted fields from a server-side blob (per-user settings)
   * or a branding-default blob (per-domain). Unknown / missing keys keep
   * their current value, so the caller can pass a partial object.
   */
  hydrateFromServer: (blob: Partial<SerializedTheme>) => void;
  setHydrating: (hydrating: boolean) => void;
  reset: () => void;
}

const initial: Omit<ThemeState, keyof ThemeActions> = {
  presetId: DEFAULT_PRESET_ID,
  mode: 'light',
  density: DEFAULT_DENSITY,
  radius: DEFAULT_RADIUS,
  sidebarPlacement: 'left',
  sidebarStyle: 'brand',
  sidebarCollapsed: false,
  custom: { primary: null },
  isHydrating: false,
};

type ThemeActions = Pick<
  ThemeState,
  | 'setPreset'
  | 'setMode'
  | 'setDensity'
  | 'setRadius'
  | 'setSidebarPlacement'
  | 'setSidebarStyle'
  | 'setSidebarCollapsed'
  | 'toggleSidebar'
  | 'setCustomPrimary'
  | 'hydrateFromServer'
  | 'setHydrating'
  | 'reset'
>;

/** Pull the persisted fields out of state for sending to the server. */
export function serializeTheme(state: ThemeState): SerializedTheme {
  return {
    presetId: state.presetId,
    mode: state.mode,
    density: state.density,
    radius: state.radius,
    sidebarPlacement: state.sidebarPlacement,
    sidebarStyle: state.sidebarStyle,
    sidebarCollapsed: state.sidebarCollapsed,
    custom: state.custom,
  };
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set) => ({
      ...initial,
      setPreset: (presetId) => set({ presetId, custom: { primary: null } }),
      setMode: (mode) => set({ mode }),
      setDensity: (density) => set({ density }),
      setRadius: (radius) => set({ radius }),
      setSidebarPlacement: (sidebarPlacement) => set({ sidebarPlacement }),
      setSidebarStyle: (sidebarStyle) => set({ sidebarStyle }),
      setSidebarCollapsed: (sidebarCollapsed) => set({ sidebarCollapsed }),
      toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
      setCustomPrimary: (primary) => set({ custom: { primary } }),
      hydrateFromServer: (blob) =>
        set((s) => ({
          presetId: blob.presetId ?? s.presetId,
          mode: blob.mode ?? s.mode,
          density: blob.density ?? s.density,
          radius: blob.radius ?? s.radius,
          sidebarPlacement: blob.sidebarPlacement ?? s.sidebarPlacement,
          sidebarStyle: blob.sidebarStyle ?? s.sidebarStyle,
          sidebarCollapsed: blob.sidebarCollapsed ?? s.sidebarCollapsed,
          custom: blob.custom ?? s.custom,
        })),
      setHydrating: (isHydrating) => set({ isHydrating }),
      reset: () => set(initial),
    }),
    {
      name: 'obd3:theme',
      version: 3,
      // `isHydrating` is a runtime flag — never persist it.
      partialize: (state) => serializeTheme(state),
      migrate: (persisted: unknown, version: number) => {
        let next = persisted as Record<string, unknown> | null;
        if (!next || typeof next !== 'object') return persisted as ThemeState;
        // v1 → v2: sidebarStyle didn't exist. Default new field to 'brand' so
        // existing users immediately see the more colorful sidebar by default.
        if (version < 2) {
          next = { ...next, sidebarStyle: next.sidebarStyle ?? 'brand' };
        }
        // v2 → v3: 'crimson' preset removed and replaced by 'blue'. Roll any
        // existing crimson user forward onto blue so their theme sheet still
        // resolves to a real preset.
        if (version < 3 && next.presetId === 'crimson') {
          next = { ...next, presetId: 'blue' };
        }
        return next as unknown as ThemeState;
      },
    },
  ),
);
